/**
 * Organ Health API client: the clinician self-service read path for the
 * organ-based digital twin — current organ scores + the organ alert feed
 * (see src/march/api/organ_alert_routes.py). Mirrors digitalTwinCoreApi's
 * auth/header/error handling so the workspace and any future admin view share
 * one contract.
 */

import { fetchWithAuth } from './fetchWithAuth';
import { getMarchHttpApiBase, getMarchRuntimeBearer, isMarchHttpApiConfigured } from './marchApiEnv';
import type { OrganBand, OrganName, OrganScore } from '../components/response/organHealth';

const getBase = (): string => getMarchHttpApiBase();
const getApiKey = (): string => (import.meta.env.VITE_COMMAND_API_KEY ?? '').trim();

function ohHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const key = getApiKey();
  if (key) h['X-API-Key'] = key;
  const bearer = getMarchRuntimeBearer();
  if (bearer) h.Authorization = `Bearer ${bearer}`;
  return h;
}

async function parseErrorDetail(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.detail === 'string') return body.detail;
    if (body?.detail) return JSON.stringify(body.detail);
  } catch {
    /* fall through */
  }
  return `Request failed (${res.status})`;
}

/** One fired organ alert, mirroring the organ_health_alert row shape. */
export interface OrganAlert {
  alert_id: string;
  patient_id: string;
  organ: string;
  level: 'critical' | 'warning';
  band: string;
  score: number | null;
  red_flags: string[];
  rationale: string;
  fired_at: string;
  /** Set when a clinician has seen + handled the alert (audit trail). */
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  /** Delivery routing derived at read time (§9.3): tier + owning role.
   * `escalated` marks an SDOH follow-up-barrier promotion (escalate-only). */
  routing?: {
    tier: 'interruptive' | 'inbox' | 'passive';
    tier_label: string;
    role: string;
    role_label: string;
    interruptive: boolean;
    rationale: string;
    escalated: boolean;
  } | null;
}

/** Mark one alert seen-and-handled (drops it from active counts; stays in the feed). */
export async function acknowledgeOrganAlert(patientId: string, alertId: string): Promise<void> {
  const pid = patientId.trim();
  const aid = alertId.trim();
  if (!pid || !aid || !isMarchHttpApiConfigured()) throw new Error('Alert API is not configured');
  const res = await fetchWithAuth(
    `${getBase()}/v1/clinical/organ-alerts/${encodeURIComponent(pid)}/${encodeURIComponent(aid)}/acknowledge`,
    { method: 'POST', extraHeaders: ohHeaders() },
  );
  if (!res.ok) throw new Error(await parseErrorDetail(res));
}

/** The scorer's transfer + penalty curve definitions (methodology, not patient
 * data). Config-derived on the backend; the single source of truth the scorer
 * itself uses, so a plotted point sits exactly on its real function. */
export interface MethodCurves {
  rubric_version: string;
  method_version: string;
  /** Each transfer-scored test → its piecewise-linear knots ([value, score]). */
  transfers: Record<string, { anchors: [number, number][] }>;
  penalties: {
    uacr: { kind: 'log'; threshold: number; k: number; cap: number };
    spo2: { kind: 'knots'; anchors: [number, number][] };
  };
}

let _curvesCache: Promise<MethodCurves | null> | null = null;

/** Fetch the method curves once per session (static methodology; cached). */
export function fetchMethodCurves(): Promise<MethodCurves | null> {
  if (_curvesCache) return _curvesCache;
  _curvesCache = (async () => {
    if (!isMarchHttpApiConfigured()) return null;
    try {
      const res = await fetchWithAuth(`${getBase()}/v1/clinical/organ-health/method-curves`, {
        method: 'GET',
        extraHeaders: ohHeaders(),
      });
      if (!res.ok) return null;
      const text = await res.text();
      return text ? (JSON.parse(text) as MethodCurves) : null;
    } catch {
      return null; // curves are an enhancement — never block the score view
    }
  })();
  return _curvesCache;
}

/** Current organ scores plus the line explaining any organ left without prose. */
export interface OrganScoreBundle {
  organs: OrganScore[];
  /**
   * Why some organs carry no `summary`, or null when they all do.
   *
   * A healthy organ with no red flags and an excellent modifiable-risk band is
   * deliberately not summarized — it costs a model round-trip to say nothing a
   * clinician acts on. But a missing summary looks identical whether it was
   * skipped on purpose or the generator failed, so the backend sends this
   * sentence and the UI must show it. Without it, silence is ambiguous in the
   * one direction that matters.
   */
  summaryNotice: string | null;
}

/** Current organ scores and the suppression notice (empty when none / not configured). */
export async function fetchOrganScoreBundle(patientId: string): Promise<OrganScoreBundle> {
  const empty: OrganScoreBundle = { organs: [], summaryNotice: null };
  const pid = patientId.trim();
  if (!pid || !isMarchHttpApiConfigured()) return empty;
  const res = await fetchWithAuth(
    `${getBase()}/v1/clinical/organ-scores/${encodeURIComponent(pid)}`,
    { method: 'GET', extraHeaders: ohHeaders() },
  );
  if (res.status === 404) return empty;
  if (!res.ok) throw new Error(await parseErrorDetail(res));
  const text = await res.text();
  if (!text) return empty;
  const body = JSON.parse(text) as { organs?: OrganScore[]; summary_notice?: string | null };
  return { organs: body.organs ?? [], summaryNotice: body.summary_notice ?? null };
}

/**
 * Current organ scores for a patient (empty list when none / not configured).
 *
 * Kept as the scores-only view for the callers that render chips and headlines
 * and have nothing to do with summary prose. Anything showing the per-organ
 * summaries should use `fetchOrganScoreBundle` instead, so it also gets the
 * notice that explains the ones without.
 */
export async function fetchOrganScores(patientId: string): Promise<OrganScore[]> {
  return (await fetchOrganScoreBundle(patientId)).organs;
}

/** One past (or current) version of an organ's score — the trend series. */
export interface OrganScoreHistoryPoint {
  score: number | null;
  band: OrganBand;
  created_at: string;
}

/** An organ's score history, newest first (empty when none / not configured). */
export async function fetchOrganScoreHistory(
  patientId: string,
  organ: OrganName,
  limit = 30,
): Promise<OrganScoreHistoryPoint[]> {
  const pid = patientId.trim();
  if (!pid || !isMarchHttpApiConfigured()) return [];
  const res = await fetchWithAuth(
    `${getBase()}/v1/clinical/organ-scores/${encodeURIComponent(pid)}/history?organ=${encodeURIComponent(organ)}&limit=${encodeURIComponent(String(limit))}`,
    { method: 'GET', extraHeaders: ohHeaders() },
  );
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(await parseErrorDetail(res));
  const text = await res.text();
  if (!text) return [];
  const body = JSON.parse(text) as { history?: OrganScoreHistoryPoint[] };
  return body.history ?? [];
}

/** A patient's organ alert feed, most-recent first. */
export async function fetchOrganAlerts(patientId: string, limit = 50): Promise<OrganAlert[]> {
  const pid = patientId.trim();
  if (!pid || !isMarchHttpApiConfigured()) return [];
  const res = await fetchWithAuth(
    `${getBase()}/v1/clinical/organ-alerts/${encodeURIComponent(pid)}?limit=${encodeURIComponent(String(limit))}`,
    { method: 'GET', extraHeaders: ohHeaders() },
  );
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(await parseErrorDetail(res));
  const text = await res.text();
  if (!text) return [];
  const body = JSON.parse(text) as { alerts?: OrganAlert[] };
  return body.alerts ?? [];
}

/**
 * Alert-stewardship metrics (admin governance view). Mirrors the backend
 * read-model in march/risk_engine/organ_health/stewardship.py.
 *
 * `suppressedAlerts` is intentionally nullable: cooldown-suppressed duplicates
 * are dropped without being recorded, so the backend reports null rather than a
 * fabricated 0. Render the accompanying note, never a zero.
 */
export interface OrganStewardship {
  window_days: number;
  tenant_id: string;
  total_alerts: number;
  patients_alerted: number;
  alerts_per_patient: number;
  by_level: Record<string, number>;
  by_organ: Record<string, number>;
  by_band: Record<string, number>;
  /** Delivery-tier split: interruptive / inbox / passive (see organ_health/tiers.py). */
  by_tier: Record<string, number>;
  interruptive_alerts: number;
  interruptive_per_patient: number;
  by_day: Record<string, number>;
  red_flag_distribution: Record<string, number>;
  acknowledged_alerts: number;
  ack_rate: number;
  unacknowledged_alerts: number;
  oldest_unacknowledged_hours: number | null;
  median_seconds_to_ack: number | null;
  p90_seconds_to_ack: number | null;
  /** `clinician` is the raw acknowledged_by id (a Keycloak UUID);
   * `clinician_label` is the username the backend resolved through the IAM
   * mirror when it could. Render the label, fall back to the id. */
  top_acknowledgers: { clinician: string; clinician_label?: string; acks: number }[];
  /** Duplicates the cooldown absorbed for alerts fired in this window. */
  suppressed_alerts: number;
  /** suppressed / (fired + suppressed) — how much noise the cooldown absorbs. */
  suppression_rate: number;
  /** Alerts absorbing the most duplicates: where the noise concentrates. */
  noisiest_alerts: {
    alert_id: string;
    patient_id: string;
    organ: string;
    level: string;
    suppressed_count: number;
    last_suppressed_at: string | null;
  }[];
  suppressed_note: string;
  generated_at: string;
}

export async function fetchOrganStewardship(windowDays = 30): Promise<OrganStewardship | null> {
  if (!isMarchHttpApiConfigured()) return null;
  const res = await fetchWithAuth(
    `${getBase()}/v1/admin/organ-health/stewardship?window_days=${encodeURIComponent(String(windowDays))}`,
    { method: 'GET', extraHeaders: ohHeaders() },
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await parseErrorDetail(res));
  const text = await res.text();
  if (!text) return null;
  return JSON.parse(text) as OrganStewardship;
}

/** Population SDOH + family coverage (admin governance view). */
export interface SdohDomainCoverage {
  domain: string;
  label: string;
  screened_patients: number;
  barrier_patients: number;
  stale_patients: number;
  coverage_rate: number;
  /** Of SCREENED patients — not of the whole panel. */
  barrier_rate_among_screened: number;
}

export interface SdohStewardship {
  tenant_id: string;
  patients_with_context: number;
  patients_fully_screened: number;
  patients_with_any_barrier: number;
  patients_with_follow_up_barrier: number;
  by_domain: SdohDomainCoverage[];
  family_patients_with_history: number;
  generated_at: string;
  /** What "panel" means here — rendered, never assumed. */
  scope_note: string;
}

export async function fetchSdohStewardship(): Promise<SdohStewardship | null> {
  if (!isMarchHttpApiConfigured()) return null;
  const res = await fetchWithAuth(`${getBase()}/v1/admin/organ-health/sdoh-stewardship`, {
    method: 'GET',
    extraHeaders: ohHeaders(),
  });
  if (!res.ok) throw new Error(await parseErrorDetail(res));
  return (await res.json()) as SdohStewardship;
}
